/**
 * Browser-safe structured logger for client-side code.
 *
 * Mirrors the Logger interface from @loci/config but avoids Node.js-only
 * modules (winston, fs) so it can be imported by 'use client' files.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LEVELS: LogLevel[] = ['debug', 'info', 'warn', 'error'];

function shouldLog(configured: LogLevel, requested: LogLevel): boolean {
  return LEVELS.indexOf(requested) >= LEVELS.indexOf(configured);
}

function getConfiguredLevel(): LogLevel {
  const level = process.env.LOG_LEVEL ?? 'info';
  return (LEVELS.includes(level as LogLevel) ? level : 'info') as LogLevel;
}

function createLogger() {
  const configuredLevel = getConfiguredLevel();
  return {
    debug(message: string, meta?: Record<string, unknown>): void {
      if (shouldLog(configuredLevel, 'debug')) {
        meta ? console.debug(message, meta) : console.debug(message);
      }
    },
    info(message: string, meta?: Record<string, unknown>): void {
      if (shouldLog(configuredLevel, 'info')) {
        meta ? console.info(message, meta) : console.info(message);
      }
    },
    warn(message: string, meta?: Record<string, unknown>): void {
      if (shouldLog(configuredLevel, 'warn')) {
        meta ? console.warn(message, meta) : console.warn(message);
      }
    },
    error(message: string, meta?: Record<string, unknown>): void {
      meta ? console.error(message, meta) : console.error(message);
    },
  };
}

export const logger = createLogger();
